<template>

  <div id="app" class="def-global">
    <!--
     @ Accessibility
     * This Element is mostly Invisible (visible=false), but when the element is visible, the Screen Reader must know, that the focus now is inside this element.
     * A bootstrap Modal works best for this, since the Focus gets stolen from the Page.
     * For a Session Timeout to not confuse the Screen-Reader User,
     * For this to work, the Fullscreen Covert needs to be: 1. shown for at least (text reading duration : 3-5 seconds) and 2A. the role=alert or 2B. the focus must point to a descriptive action
     -->
    <b-modal :visible="appIsIdle" hide-footer hide-header no-close-on-esc no-close-on-backdrop title-sr-only title="">
      <UnstyledButton :action="logout" ref="appIdleFocus" class="border-0" style="width: 100%">
        <FlexLayout direction="down" inline center>
          <!-- @ Accessibility : make sure this text is Visible! AKA the bg-color != transparent AND BG ALPHA IS NEVER BELOW 1.0 OPACITY or ALPHA! -->
          <p>{{ $t('text.sessionTerminated') }}</p>
          <div style="height:50dvh; width:80%">
            <img class="imageFitsParent" src="@/assets/images/excl-point-inverse.png" alt="!" role="presentation"/>
          </div>
        </FlexLayout>
      </UnstyledButton>
    </b-modal>

    <div v-bind:class="{ appIsIdle }" class="bodyScroller">

      <!--
       @ Accessibility
       * This Element is called a "Skip Link". On Most pages who do not have a default Focus, the Screen reader takes the first Focusable Element to use.
       * Clicking this element will replace the current History Entry with a "#main" that will skip the focus to an elemen named "main"
       -->
      <div style="position: fixed; z-index: 99999; left:2%" ><a class="sr-only sr-only-focusable bg-dark text-light" href="#main">{{ $t('text.skipToMainContent') }}</a></div>

      <Header />

      <!--
      @ Accessibility
       * This Element will gain focus on each Page Load, if there is *NO* autofocus element.
       * But since this element has neiter a role, a hitbox, a focus target, it is invisible and still can be called with focus() and blur()
      -->
      <button class="sr-only" href="javascript:void(0)" aria-hidden="true" ref="focusHelper" tabindex="-1" role="none" aria-description="Accessibility Auto Focus Helper (ignore this element)"></button>
      <!-- /Accessability -->
      <div id="scrollingContent">
        <!-- todo PR - make a solution, so the "aria-label"s value below has on all pages the Pagename in.
              Mostly it works, except for Login! SR reads: "Willkommen zurück., Hauptlandmarke" instead of "Willkommen zurück, Login D2Phy, Hauptlandmarke" -->
        <main id="main" :aria-label="$t(this.$route.meta.title)">
          <router-view />
        </main>
        <Footer :links="config.FOOTER.links" />
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-loading-overlay/dist/vue-loading.css'
import './css/app.scss'
import {isAppIdle, SessionTimer} from '@/store/SessionTimer'
import {useLocaleStore} from "@/stores/locale";
import {useAuthStore} from "@/stores/auth";
import {computed, ref} from "vue";
import {accountService} from "@/services/api/accountService";
import {useAppStore} from "@/stores/app";
import {Mode} from "@/constants/constants";
import Footer from "@/components/shell/Footer.vue";
import Header from "@/components/shell/Header.vue";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import UnstyledButton from "@/components/actions/UnstyledButton.vue";

export default {
  name: 'App',
  setup() {
    const appStore = useAppStore();
    const localeStore = useLocaleStore();
    const authStore = useAuthStore();

    return {
      appStore,
      localeStore,
      authStore
    };
  },
  computed: {
    config() {
      return window.config;
    },
    currentLocale() {
      return this.localeStore.getLocale;
    },
    appIsIdle(){
      return isAppIdle();
    },
  },
  components: {
    FlexLayout,
    UnstyledButton,
    Footer,
    Header,
  },
  destroyed() {
    this.timer.onUnmount();
  },
  watch:{
    '$route': function () {
      this.updatePageTitle();
      this.checkLoginCondition();

      // @ Accessibility: Focus got lost after a page navigation. The Focus now moves to the Hidden Accessible after the page loads.
      const focusHelper = this.$refs.focusHelper;
      this.$nextTick().then(() => { // wait for elements to be created;
        this.scrollPageToTop();
        if (document.activeElement === document.body || document.activeElement === null) { // test, that the focus was really lost on the document
          if (focusHelper)
          {
            focusHelper.focus({ preventScroll:true, focusVisible:false }); // focusVisible still in experimental - use blur to hide the focus as fallback
            focusHelper.blur();
          }
        }
      })
    },
    'currentLocale': function () { // on lanugage change
      this.updatePageTitle();
    },
    'appIsIdle': function (value){
      if (value) {
        setTimeout(this.logout(true),5000); // @ Accessibility: timer, so the user has time to read the "sessionExpired"-text, before he gets logged out.
      }
    },
    'authStore.isLoggedIn': function (){
      this.checkLoginCondition();
    },
  },
  methods: {
    /** Proxify this
     * @param {function(this:ThisType,...):T} f
     * @returns {function(...):T}
     * */
    t(f){
      const _t = f.bind(this);
      return function thisProxy(...a){return _t(...a);};
    },
    checkLoginCondition(){
      if (typeof this.$route.meta !== 'undefined' && this.$route.meta.requiresLogin === true){
        if (!this.authStore.isLoggedIn){
          this.logout(true);
        }
      }
    },
    refreshPage() {
      this.logout();
    },
    scrollPageToTop() {
      const scrollingContent = document.getElementById('scrollingContent');
      if (scrollingContent) {
        scrollingContent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    },
    handleMobileKeyboardChanges(event) {
      const { height } = event.target.boundingRect;
      document.body.style.setProperty("--keyboardHeight",height+"px","");
      document.querySelector(".fontTitle").innerText=height;
    },
    updatePageTitle(){
      let pageTitle = "";
      if (typeof this.$route?.meta?.title === 'string') {
        pageTitle = this.$t(this.$route.meta.title);
      } else {
        pageTitle = this.$route.name;
      }
      document.title = this.$t('system.title.application') + ": " + pageTitle;
    },
    repeat() {
      var cookiesEnabled = true;
      var cookieEnabled = navigator.cookieEnabled;

      // When cookieEnabled flag is present and false then cookies are disabled.
      if (cookieEnabled === false) {
        cookiesEnabled = false;
      }

      // try to set a test cookie if we can't see any cookies and we're using
      // either a browser that doesn't support navigator.cookieEnabled
      // or IE (which always returns true for navigator.cookieEnabled)
      if (!document.cookie && (cookieEnabled === null || /*@cc_on!@*/false)) {
        document.cookie = "testcookie=1";

        if (!document.cookie) {
          cookiesEnabled = false;
        } else {
          document.cookie = "testcookie=; expires=" + new Date(0).toUTCString();
        }
      }

      if (!cookiesEnabled) {
        console.log("cookies are not enabled");
      }
    },
    async logout(keepRememberMe = false) {
      clearInterval(this.intervalId);

      await accountService.logout(keepRememberMe);
      this.authStore.setLoginChecked(false);

      if (this.appStore.isMode(Mode.TABLE)) {
        this.$navigateToErrorLanding(true);
      } else if (this.appStore.isMode(Mode.IMMEDIATE)) {
        this.$navigateToRegisterGuest();
      } else {
        this.$navigateToLogin();
      }
    }
  },
  data() {
    return {
      isConfigLoaded: false,
      configLoaded: {},
      mobileView: true,
      showNav: false,
      intervalId: 0,
      timer: undefined,
    }
  },
  provide(){
    let metaPageTitle = ref(undefined);
    let global_providers=ref({});

    const providable = {
      resetPageMetaTitle: function (){
        if (metaPageTitle.value !== undefined) {
          metaPageTitle.value = undefined;
        }
      },
      setPageMetaTitle: async function(name) {
        // waits for the route to reset the page MetaTitle
        this.$nextTick().then(
          function (){
            metaPageTitle.value=name;
          }
        );
      },
      getPageMetaTitle: computed(()=>metaPageTitle && metaPageTitle.value),
      /**
       * @param {string} key
       * @param {any} value
       * */
      'provideWith':function(key,value){
        global_providers.value[key] = value;
        // providable[key]=value;
      },
      /**
       * @param {string} key
       * */
      'findInject':function(key){
        return global_providers.value[key];
      },
    };

    // Object.keys(global_providers.value).forEach(function(key){
    //   providable[key] = global_providers.value[key];
    // });

    return providable;
  },
  async mounted() {
    this.timer = SessionTimer();
    this.timer.onMount();

    await this.localeStore.setLocale(this.currentLocale);

    if (this.intervalId !== 0) {
      clearInterval(this.intervalId);
    }
    this.intervalId = setInterval(this.repeat, 1000);

    //BIND EVENTS

    if ("virtualKeyboard" in navigator) {
      navigator.virtualKeyboard.overlaysContent = true;
      navigator.virtualKeyboard.addEventListener("geometrychange", this.handleMobileKeyboardChanges);
    }
  },
}
</script>
<style scoped>
</style>
